import { useState } from "react";
import { Card, CardContent } from "@/frontend/components/ui/card";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/frontend/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/frontend/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/frontend/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/frontend/components/ui/alert-dialog";
import { Textarea } from "@/frontend/components/ui/textarea";
import { Label } from "@/frontend/components/ui/label";
import { StatusBadge } from "@/frontend/components/admin/StatusBadge";
import { StudentCredentialsModal } from "@/frontend/components/admin/StudentCredentialsModal";
import { Search, Eye, Check, X, Loader2, Mail, Home, Plus, Trash2, Pencil, Ban } from "lucide-react";
import { Booking } from "@/shared/types";
import { useToast } from "@/backend/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import { format } from "date-fns";
import { monthsInclusive } from "@/shared/utils/monthRange";
import { GuestDetailsBlock } from "@/frontend/components/admin/GuestDetailsBlock";
import { useAdminBookings, useUpdateBookingStatus, useApproveBookingWithStudent, useApprovedBookingsPasswordStatus, useDeleteAdminBooking, useCancelApprovedBooking } from "@/backend/hooks/admin/useAdminBookings";
import { CreateAdminBookingDialog } from "@/frontend/components/admin/CreateAdminBookingDialog";
import { useAdminResidences } from "@/backend/hooks/admin/useAdminResidences";
import { useAdminRoomTypes } from "@/backend/hooks/admin/useAdminRoomTypes";
import { sendBookingRejectionEmail, sendBookingApprovedEmail, resendStudentCredentials } from "@/backend/services/email/emailClient";
import { getRoomLabel } from "@/backend/lib/roomUtils";

const AdminBookings = () => {
  const { toast } = useToast();
  const { data: bookings = [], isLoading: bookingsLoading } = useAdminBookings();
  const { data: residences = [], isLoading: residencesLoading } = useAdminResidences();
  const { data: roomTypes = [], isLoading: roomTypesLoading } = useAdminRoomTypes();
  const updateBookingStatus = useUpdateBookingStatus();
  const approveWithStudent = useApproveBookingWithStudent();
  const deleteAdminBooking = useDeleteAdminBooking();

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<Booking | null>(null);
  const [bookingToDelete, setBookingToDelete] = useState<Booking | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(null);
  const [rejectionComment, setRejectionComment] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isResendingCredentials, setIsResendingCredentials] = useState(false);
  // Cancel-approved-booking dialog
  const [bookingToCancel, setBookingToCancel] = useState<Booking | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const cancelApprovedBooking = useCancelApprovedBooking();

  const { data: passwordStatusMap } = useApprovedBookingsPasswordStatus(bookings);
  const passwordStatus = selectedBooking ? passwordStatusMap?.get(selectedBooking.id) : undefined;

  // Student credentials modal state
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [studentCredentials, setStudentCredentials] = useState<Array<{
    name: string;
    email: string;
    password: string;
  }> | null>(null);

  const filteredBookings = bookings.filter((b) => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      q === "" ||
      b.guests.some(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          g.email.toLowerCase().includes(q)
      ) ||
      (b.adminNote?.toLowerCase().includes(q) ?? false);
    const matchesStatus = statusFilter === "all" || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openEditEntry = (booking: Booking) => {
    setEditEntry(booking);
    setIsCreateOpen(true);
  };

  const handleDeleteAdminBooking = async () => {
    if (!bookingToDelete) return;
    try {
      await deleteAdminBooking.mutateAsync(bookingToDelete.id);
      sonnerToast.success(
        bookingToDelete.status === "blocked" ? "Block removed" : "Admin booking deleted"
      );
      setBookingToDelete(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete entry";
      sonnerToast.error("Could not delete entry", { description: message });
    }
  };

  const handleCancelApproved = async () => {
    if (!bookingToCancel) return;
    const booking = bookingToCancel;
    const trimmed = cancelReason.trim() || undefined;
    try {
      const result = await cancelApprovedBooking.mutateAsync({ bookingId: booking.id, reason: trimmed });
      const kept = result.keptAccounts?.length ?? 0;
      sonnerToast.success("Booking cancelled", {
        description:
          kept > 0
            ? `Unit freed. ${result.deletedAccounts} account(s) removed, ${kept} kept (contract or other booking).`
            : `Unit freed. ${result.deletedAccounts} student account(s) removed.`,
      });
      setBookingToCancel(null);
      setCancelReason("");
      setIsDetailModalOpen(false);
      // Notify the guests, same as a rejection.
      sendRejectionEmails(booking, trimmed);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to cancel booking";
      sonnerToast.error("Could not cancel booking", { description: message });
    }
  };

  const sendRejectionEmails = async (booking: Booking, comment?: string) => {
    const residence = residences.find((r) => r.id === booking.residenceId);
    const roomType = roomTypes.find((rt) => rt.id === booking.roomTypeId);
    setIsSendingEmail(true);

    try {
      const results = await Promise.all(
        booking.guests.map((guest) =>
          sendBookingRejectionEmail({
            to: guest.email,
            guestName: guest.name,
            residenceName: residence?.name || "Unknown",
            roomTypeName: roomType?.name || "Not specified",
            checkIn: format(booking.checkIn.toDate(), "MMMM d, yyyy"),
            checkOut: format(booking.checkOut.toDate(), "MMMM d, yyyy"),
            rejectionComment: comment || undefined,
          })
        )
      );

      const allSuccess = results.every((r) => r.success);
      if (allSuccess) {
        sonnerToast.success("Email notification sent successfully", {
          description: `Sent to ${booking.guests.map((g) => g.email).join(", ")}`,
        });
      } else {
        sonnerToast.error("Failed to send email notification", {
          description: "Some emails could not be sent. Please try again.",
        });
      }
    } catch {
      sonnerToast.error("Failed to send email notification", {
        description: "Failed to send rejection email. Please try again.",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleResendCredentials = async (booking: Booking) => {
    setIsResendingCredentials(true);
    try {
      const portalUrl = `${window.location.origin}/student/login`;
      const { credentials, failedEmails } = await resendStudentCredentials(booking.id, portalUrl);
      setStudentCredentials(credentials);
      setCredentialsModalOpen(true);

      const sent = credentials
        .map((c) => c.email)
        .filter((e) => !failedEmails?.includes(e));

      if (!failedEmails || failedEmails.length === 0) {
        sonnerToast.success("Credentials regenerated", {
          description: `New credentials emailed to ${sent.join(", ")}`,
        });
      } else {
        sonnerToast.error("Credentials regenerated · some emails failed", {
          description: `Failed: ${failedEmails.join(", ")}. Use the modal to share manually.`,
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to resend credentials";
      sonnerToast.error("Failed to resend credentials", { description: errorMessage });
    } finally {
      setIsResendingCredentials(false);
    }
  };

  const openDetailModal = (booking: Booking) => {
    setSelectedBooking(booking);
    setIsDetailModalOpen(true);
  };

  const openConfirmDialog = (booking: Booking, action: "approve" | "reject") => {
    setSelectedBooking(booking);
    setActionType(action);
    setRejectionComment("");
    setIsConfirmDialogOpen(true);
  };

  const handleStatusChange = async () => {
    if (!selectedBooking || !actionType) return;

    try {
      if (actionType === "approve") {
        const { credentials: result, reassigned } = await approveWithStudent.mutateAsync({ booking: selectedBooking });
        setStudentCredentials(result);
        setCredentialsModalOpen(true);
        if (reassigned) {
          sonnerToast.info("Assigned to another free unit of the same typology", {
            description: "The originally assigned unit was already taken.",
          });
        }

        const residence = residences.find((r) => r.id === selectedBooking.residenceId);
        const room = getRoomLabel(selectedBooking.roomTypeId, roomTypes);
        const portalUrl = `${window.location.origin}/student/login`;
        const checkIn = format(selectedBooking.checkIn.toDate(), "MMMM d, yyyy");
        const checkOut = format(selectedBooking.checkOut.toDate(), "MMMM d, yyyy");

        const emailResults = await Promise.all(
          result.map((cred) =>
            sendBookingApprovedEmail({
              to: cred.email,
              guestName: cred.name,
              loginEmail: cred.email,
              tempPassword: cred.password,
              residenceName: residence?.name || "Unknown",
              roomTypeName: room.name,
              checkIn,
              checkOut,
              portalUrl,
            }).then((r) => ({ email: cred.email, success: r.success }))
          )
        );

        const sent = emailResults.filter((r) => r.success).map((r) => r.email);
        const failed = emailResults.filter((r) => !r.success).map((r) => r.email);

        if (failed.length === 0) {
          sonnerToast.success("Booking approved", {
            description: `Credentials emailed to ${sent.join(", ")}`,
          });
        } else {
          sonnerToast.error("Booking approved · some emails failed", {
            description: `Failed: ${failed.join(", ")}. Use the credentials modal to share manually.`,
          });
        }
      } else {
        // Reject booking (no student creation)
        const trimmedComment = rejectionComment.trim() || undefined;
        await updateBookingStatus.mutateAsync({
          id: selectedBooking.id,
          status: "rejected",
          rejectionComment: trimmedComment,
        });

        toast({
          title: "Success",
          description: "Booking rejected",
        });

        // Send rejection email to all guests
        sendRejectionEmails(selectedBooking, trimmedComment);
      }

      setIsConfirmDialogOpen(false);
      setIsDetailModalOpen(false);
    } catch (err) {
      const errorMessage = err.message || "Failed to process booking. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  if (bookingsLoading || residencesLoading || roomTypesLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            name="booking-search"
            autoComplete="off"
            placeholder="Search by name, email or note..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          onClick={() => {
            setEditEntry(null);
            setIsCreateOpen(true);
          }}
          className="sm:self-start"
        >
          <Plus className="h-4 w-4 mr-2" />
          New booking / block
        </Button>
      </div>
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="blocked">Blocked</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Desktop table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Residence</TableHead>
                  <TableHead>Room</TableHead>
                  <TableHead>Check-in / Check-out</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBookings.map((booking) => {
                  const residence = residences.find((r) => r.id === booking.residenceId);
                  const room = getRoomLabel(booking.roomTypeId, roomTypes);
                  return (
                    <TableRow key={booking.id}>
                      <TableCell>
                        {booking.status === "blocked" ? (
                          <div>
                            <p className="font-medium">Admin block</p>
                            {booking.adminNote && (
                              <p className="text-sm text-muted-foreground">{booking.adminNote}</p>
                            )}
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium">{booking.guests[0]?.name}</p>
                            <p className="text-sm text-muted-foreground">{booking.guests[0]?.email}</p>
                            {booking.guests[0]?.phone && (
                              <p className="text-sm text-muted-foreground">{booking.guests[0].phone}</p>
                            )}
                            {booking.guests.length > 1 && (
                              <p className="text-xs text-muted-foreground">+{booking.guests.length - 1} more</p>
                            )}
                            {booking.type === "admin" && (
                              <span className="mt-1 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                                Offline booking
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{residence?.name || "Unknown"}</TableCell>
                      <TableCell>
                        <div>
                          <p>{room.name}</p>
                          {room.typologyKey && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Home className="h-3 w-3" />
                              {room.typologyKey}
                              {room.unitNumber && ` · Unit ${room.unitNumber}`}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{format(booking.checkIn.toDate(), "MMM d, yyyy")}</p>
                          <p className="text-muted-foreground">{format(booking.checkOut.toDate(), "MMM d, yyyy")}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={booking.status} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(booking.createdAt.toDate(), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        {booking.status !== "blocked" && (
                          <Button variant="ghost" size="icon" onClick={() => openDetailModal(booking)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {booking.type === "admin" && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditEntry(booking)}
                              title="Edit"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="hover:bg-destructive group"
                              onClick={() => setBookingToDelete(booking)}
                              title={booking.status === "blocked" ? "Remove block" : "Delete admin booking"}
                            >
                              <Trash2 className="h-4 w-4 text-destructive group-hover:text-white" />
                            </Button>
                          </>
                        )}
                        {booking.status === "pending" && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="hover:bg-emerald-600 group"
                              onClick={() => openConfirmDialog(booking, "approve")}
                            >
                              <Check className="h-4 w-4 text-emerald-600 group-hover:text-white" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="hover:bg-destructive group"
                              onClick={() => openConfirmDialog(booking, "reject")}
                            >
                              <X className="h-4 w-4 text-destructive group-hover:text-white" />
                            </Button>
                          </>
                        )}
                        {booking.status === "approved" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleResendCredentials(booking)}
                            disabled={
                              isResendingCredentials ||
                              passwordStatusMap?.get(booking.id)?.anyChanged === true
                            }
                            title={
                              passwordStatusMap?.get(booking.id)?.anyChanged
                                ? "Password already changed by the student"
                                : "Resend credentials"
                            }
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        )}
                        {booking.status === "approved" && booking.type !== "admin" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="hover:bg-destructive group"
                            onClick={() => { setCancelReason(""); setBookingToCancel(booking); }}
                            title="Cancel booking (frees the unit, removes student accounts)"
                          >
                            <Ban className="h-4 w-4 text-destructive group-hover:text-white" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {filteredBookings.map((booking) => {
          const residence = residences.find((r) => r.id === booking.residenceId);
          const room = getRoomLabel(booking.roomTypeId, roomTypes);
          return (
            <Card key={booking.id}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  {booking.status === "blocked" ? (
                    <div className="min-w-0">
                      <p className="font-medium truncate">Admin block</p>
                      {booking.adminNote && (
                        <p className="text-sm text-muted-foreground truncate">{booking.adminNote}</p>
                      )}
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <p className="font-medium truncate">{booking.guests[0]?.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{booking.guests[0]?.email}</p>
                      {booking.guests[0]?.phone && (
                        <p className="text-sm text-muted-foreground truncate">{booking.guests[0].phone}</p>
                      )}
                      {booking.guests.length > 1 && (
                        <p className="text-xs text-muted-foreground">+{booking.guests.length - 1} more</p>
                      )}
                      {booking.type === "admin" && (
                        <span className="mt-1 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          Offline booking
                        </span>
                      )}
                    </div>
                  )}
                  <StatusBadge status={booking.status} className="flex-shrink-0" />
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">{residence?.name || "Unknown"}</p>
                  <p>
                    Room: {room.name}
                    {room.typologyKey && (
                      <span className="ml-1 inline-flex items-center gap-1">
                        <Home className="h-3 w-3" />
                        {room.typologyKey}
                        {room.unitNumber && ` · Unit ${room.unitNumber}`}
                      </span>
                    )}
                  </p>
                  <p>Check-in: {format(booking.checkIn.toDate(), "MMM d, yyyy")}</p>
                  <p>Check-out: {format(booking.checkOut.toDate(), "MMM d, yyyy")}</p>
                  <p>Created: {format(booking.createdAt.toDate(), "MMM d, yyyy")}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 border-t">
                  {booking.status !== "blocked" && (
                    <Button variant="outline" size="sm" className="w-full" onClick={() => openDetailModal(booking)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Details
                    </Button>
                  )}
                  {booking.type === "admin" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => openEditEntry(booking)}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-destructive border-destructive/20 hover:bg-destructive/10"
                        onClick={() => setBookingToDelete(booking)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {booking.status === "blocked" ? "Remove" : "Delete"}
                      </Button>
                    </>
                  )}
                  {booking.status === "pending" && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => openConfirmDialog(booking, "approve")}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-destructive border-destructive/20 hover:bg-destructive/10"
                        onClick={() => openConfirmDialog(booking, "reject")}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  {booking.status === "approved" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleResendCredentials(booking)}
                      disabled={
                        isResendingCredentials ||
                        passwordStatusMap?.get(booking.id)?.anyChanged === true
                      }
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Resend
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4 py-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Status</span>
                <StatusBadge status={selectedBooking.status} />
              </div>

              <div className="border-t pt-4">
                <GuestDetailsBlock guests={selectedBooking.guests} />
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Stay Details</h4>
                <dl className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Residence</dt>
                    <dd>{residences.find((r) => r.id === selectedBooking.residenceId)?.name || "Unknown"}</dd>
                  </div>
                  {(() => {
                    const room = getRoomLabel(selectedBooking.roomTypeId, roomTypes);
                    return (
                      <div className="flex justify-between">
                        <dt className="text-muted-foreground">Room</dt>
                        <dd className="text-right">
                          {room.name}
                          {room.typologyKey && (
                            <span className="flex items-center justify-end gap-1 text-xs text-muted-foreground">
                              <Home className="h-3 w-3" />
                              {room.typologyKey}
                              {room.unitNumber && ` · Unit ${room.unitNumber}`}
                            </span>
                          )}
                        </dd>
                      </div>
                    );
                  })()}
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Billing period</dt>
                    <dd className="text-right">
                      {format(selectedBooking.checkIn.toDate(), "MMMM d, yyyy")} – {format(selectedBooking.checkOut.toDate(), "MMMM d, yyyy")}
                      <span className="block text-xs text-muted-foreground">
                        {monthsInclusive(selectedBooking.checkIn.toDate(), selectedBooking.checkOut.toDate())} months billed
                      </span>
                    </dd>
                  </div>
                  {selectedBooking.arrivalDate && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Arrival</dt>
                      <dd className="font-medium">{format(selectedBooking.arrivalDate.toDate(), "MMMM d, yyyy")}</dd>
                    </div>
                  )}
                  {selectedBooking.departureDate && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Departure</dt>
                      <dd className="font-medium">{format(selectedBooking.departureDate.toDate(), "MMMM d, yyyy")}</dd>
                    </div>
                  )}
                  {selectedBooking.reference && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Payment reference</dt>
                      <dd className="font-mono font-medium">{selectedBooking.reference}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {selectedBooking.notes && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedBooking.notes}</p>
                </div>
              )}

              {selectedBooking.status === "rejected" && selectedBooking.rejectionComment && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2 text-destructive">Rejection Reason</h4>
                  <p className="text-sm text-muted-foreground">{selectedBooking.rejectionComment}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {selectedBooking?.status === "pending" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => openConfirmDialog(selectedBooking, "reject")}
                  className="text-destructive"
                  disabled={updateBookingStatus.isPending}
                >
                  Reject
                </Button>
                <Button
                  onClick={() => openConfirmDialog(selectedBooking, "approve")}
                  disabled={updateBookingStatus.isPending}
                >
                  Approve
                </Button>
              </>
            )}
            {selectedBooking?.status === "rejected" && (
              <Button
                variant="outline"
                onClick={() => sendRejectionEmails(selectedBooking, selectedBooking.rejectionComment)}
                disabled={isSendingEmail}
              >
                {isSendingEmail ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Mail className="mr-2 h-4 w-4" />
                )}
                Resend Email
              </Button>
            )}
            {selectedBooking?.status === "approved" && (
              <div className="flex flex-col items-end gap-1">
                <Button
                  variant="outline"
                  onClick={() => handleResendCredentials(selectedBooking)}
                  disabled={isResendingCredentials || passwordStatus?.anyChanged === true}
                >
                  {isResendingCredentials ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Mail className="mr-2 h-4 w-4" />
                  )}
                  Resend Credentials
                </Button>
                {passwordStatus?.anyChanged && (
                  <p className="text-xs text-muted-foreground">
                    Password already changed by {passwordStatus.changedEmails.join(", ")}.
                  </p>
                )}
              </div>
            )}
            {selectedBooking?.status !== "pending" && (
              <Button variant="outline" onClick={() => setIsDetailModalOpen(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Confirm Dialog */}
      <AlertDialog open={isConfirmDialogOpen && actionType === "approve"} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approve Booking</AlertDialogTitle>
            <AlertDialogDescription>
              {`Approving this booking will create ${selectedBooking && selectedBooking.guests.length > 1 ? `${selectedBooking.guests.length} student accounts` : `a student account for ${selectedBooking?.guests[0]?.name}`}. Are you sure?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={approveWithStudent.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStatusChange}
              disabled={approveWithStudent.isPending}
            >
              {approveWithStudent.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Approve & Create Account"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Confirm Dialog */}
      <Dialog open={isConfirmDialogOpen && actionType === "reject"} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Booking</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to reject this booking from {selectedBooking?.guests[0]?.name}?
          </p>
          <div className="space-y-2">
            <Label htmlFor="rejection-comment">
              Rejection reason <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="rejection-comment"
              placeholder="Explain why this booking is being rejected..."
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmDialogOpen(false)}
              disabled={updateBookingStatus.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStatusChange}
              disabled={updateBookingStatus.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {updateBookingStatus.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Rejecting...
                </>
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student Credentials Modal */}
      {studentCredentials && (
        <StudentCredentialsModal
          open={credentialsModalOpen}
          onClose={() => {
            setCredentialsModalOpen(false);
            setStudentCredentials(null);
          }}
          students={studentCredentials}
        />
      )}

      {/* Create / edit block or offline booking */}
      <CreateAdminBookingDialog
        open={isCreateOpen}
        onOpenChange={(o) => {
          setIsCreateOpen(o);
          if (!o) setEditEntry(null);
        }}
        editEntry={editEntry}
      />

      {/* Delete admin entry confirm */}
      <AlertDialog open={!!bookingToDelete} onOpenChange={(open) => !open && setBookingToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bookingToDelete?.status === "blocked" ? "Remove availability block" : "Delete admin booking"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bookingToDelete?.status === "blocked"
                ? "This will free up the unit for these dates and make it bookable again."
                : "This will permanently delete this admin booking. Student accounts already created are not removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAdminBooking.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAdminBooking();
              }}
              disabled={deleteAdminBooking.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteAdminBooking.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : bookingToDelete?.status === "blocked" ? (
                "Remove block"
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel an approved booking */}
      <AlertDialog open={!!bookingToCancel} onOpenChange={(open) => { if (!open && !cancelApprovedBooking.isPending) { setBookingToCancel(null); setCancelReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this approved booking?</AlertDialogTitle>
            <AlertDialogDescription>
              This frees the unit for these dates and removes the student login account(s)
              created for this booking. Students who have a contract or another active
              booking are kept. The guest is notified by email. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-1">
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason (optional, included in the email to the guest)"
              rows={2}
              disabled={cancelApprovedBooking.isPending}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelApprovedBooking.isPending}>Keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCancelApproved(); }}
              disabled={cancelApprovedBooking.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {cancelApprovedBooking.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cancelling...
                </>
              ) : (
                "Cancel booking"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminBookings;
